/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { attributePrioritiesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/attributePrioritiesEntryPoint.entrypoint.tsx';
import { cleansingCategoriesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/cleansingCategoriesEntryPoint.entrypoint.tsx';
import { customDateFormatsEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/customDateFormatsEntryPoint.entrypoint.tsx';
import { dataSourcePrioritiesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/dataSourcePrioritiesEntryPoint.entrypoint.tsx';
import { dataSourceTypesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/dataSourceTypesEntryPoint.entrypoint.tsx';
import { defaultCleansingRulesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/defaultCleansingRulesEntryPoint.entrypoint.tsx';
import { auditLogEntryPoint } from '@jira/assets-app__data-manager-audit-log/src/auditLogEntryPoint.entrypoint.tsx';
import type {
	ObjectClassesSettingsEntryPoints,
	ObjectClassesSettingsType,
} from '@jira/assets-app__data-manager-common/src/ObjectListViewTypes.types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

type ExtraProps = {
	settingType?: ObjectClassesSettingsType;
};

const SETTING_TYPES: readonly ObjectClassesSettingsType[] = [
	'data-source-type',
	'data-source-priorities',
	'attribute-priorities',
	'default-cleansing-rules',
	'cleansing-categories',
	'custom-date-formats',
	'audit-log',
] as const;

const SETTINGS: Partial<Record<ObjectClassesSettingsType, ReturnType<typeof createEntryPoint>>> = {
	'attribute-priorities': attributePrioritiesEntryPoint,
	'cleansing-categories': cleansingCategoriesEntryPoint,
	'data-source-type': dataSourceTypesEntryPoint,
	'data-source-priorities': dataSourcePrioritiesEntryPoint,
	'default-cleansing-rules': defaultCleansingRulesEntryPoint,
	'custom-date-formats': customDateFormatsEntryPoint,
	'audit-log': auditLogEntryPoint,
};

const isValidSettingType = (settingType: unknown): settingType is ObjectClassesSettingsType => {
	if (typeof settingType !== 'string') {
		return false;
	}
	return SETTING_TYPES.some((validType) => validType === settingType);
};

const getEntryPoint = (
	settingType: ObjectClassesSettingsType,
): ReturnType<typeof createEntryPoint> | undefined => {
	if (
		(settingType === 'data-source-priorities' || settingType === 'attribute-priorities') &&
		fg('assets_data_manager_fast_follow')
	) {
		return undefined;
	}

	if (settingType === 'custom-date-formats' && !fg('assets_data_manager_fast_follow')) {
		return undefined;
	}

	return SETTINGS[settingType];
};

type AssetsDataManagerObjectClassesSettingsEntryPointComponent = EntryPointComponent<
	{},
	ObjectClassesSettingsEntryPoints,
	{},
	ExtraProps
>;

export const assetsDataManagerObjectClassesSettingsEntryPoint: EntryPoint<
	AssetsDataManagerObjectClassesSettingsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<AssetsDataManagerObjectClassesSettingsEntryPointComponent>(
		/* webpackChunkName: "async-assets-data-manager-object-classes-settings", jiraSpaEntry: "async-assets-data-manager-object-classes-settings" */ './AssetsDataManagerObjectClassesSettingsPageSpa.tsx',
	),
	getPreloadProps: ({
		context: {
			match: { params },
		},
	}: EntryPointRouteParams) => {
		const validSettingType = isValidSettingType(params?.settingType)
			? params.settingType
			: undefined;

		if (validSettingType) {
			const entryPoint = getEntryPoint(validSettingType);
			if (entryPoint) {
				return {
					entryPoints: {
						[validSettingType]: {
							entryPoint,
							entryPointParams: {},
						},
					},
					extraProps: {
						settingType: validSettingType,
					},
				};
			}
		}

		return {
			entryPoints: {},
			extraProps: {
				settingType: validSettingType,
			},
		};
	},
});
